import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const XmlToJson = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );

  const parseXml = (xmlStr) => {
    const parser = new DOMParser();
    const xml = parser.parseFromString(xmlStr, 'text/xml');
    const obj = xmlToJson(xml.documentElement);
    return obj;
  };

  const xmlToJson = (node) => {
    let result = {};

    if (node.nodeType === 1) { // element
      if (node.attributes.length > 0) {
        result['@attributes'] = {};
        Array.from(node.attributes).forEach(attr => {
          result['@attributes'][attr.nodeName] = attr.nodeValue;
        });
      }

      if (node.childNodes.length === 1 && node.firstChild.nodeType === 3) {
        return node.textContent;
      }

      Array.from(node.childNodes).forEach(child => {
        if (child.nodeType === 1) {
          const childJson = xmlToJson(child);
          if (!result[child.nodeName]) {
            result[child.nodeName] = childJson;
          } else {
            if (!Array.isArray(result[child.nodeName])) {
              result[child.nodeName] = [result[child.nodeName]];
            }
            result[child.nodeName].push(childJson);
          }
        }
      });
    }
    return result;
  };

  const handleConvert = () => {
    try {
      const json = parseXml(inputCode);
      const pretty = JSON.stringify(json, null, 2);
      setOutputCode(pretty);
    } catch (e) {
      setOutputCode("// ❌ Invalid XML:\n" + e.message);
    }
  };

  return (
    <div className="formatter-container">
      <h1> XML ➝ JSON Converter</h1>

      {/* Toolbar */}
      <div className="toolbar">
        <button onClick={handleConvert} className="btn primary">
          <FaBrush /> Convert
        </button>
        <button onClick={() => handleDownload("json", "formatted-json")} className="btn success">
          <FaDownload /> Download
        </button>
		<button onClick={handleClear} className="btn danger">
          <FaTrash /> Clear
        </button>

      </div>

      {/* Editors */}
      <div className="editor-panels">
        <JsonEditorPanel title="Input Xml" language="json" code={inputCode} setCode={setInputCode} />
        <div className="editor-section-empty-div">
        </div>
        <JsonEditorPanel title="Output JSON" language="json"  code={outputCode} />
      </div>
	  
	  <CommonSeoContent
  title="Online XML to JSON Converter & Formatter"
  description="Convert XML data into JSON instantly with jsonconverter.org. Supports attributes, nested elements, and produces clean, readable JSON output."
  keywords="XML to JSON, xml to json converter, xml to json online, xml to json tool, xml parser, xml formatter, jsonconverter.org"
  author="jsonconverter.org"
  heading="Why Use XML to JSON Converter?"
  whyUseText="Our XML to JSON Converter allows you to easily transform structured XML data into clean, readable JSON format. This is especially useful when working with APIs, legacy systems, or configuration files that provide data in XML format but require processing in JavaScript or other modern languages that use JSON. It supports nested elements, attributes, and mixed content, ensuring accurate and complete conversion. Because everything runs directly in your browser, your data stays secure and private. No need to install any software — it's fast, reliable, and completely free to use."
  howToUseSteps={[
    "Paste your XML data into the input editor.",
    "Click the Convert button.",
    "Instantly view the converted JSON output.",
    "Copy or download the JSON for use in your application.",
  ]}
  features={[
    "Converts XML to well-structured, readable JSON format",
    "Supports nested XML elements and attributes",
    "Handles mixed content and complex hierarchies",
    "Fully in-browser conversion — your data never leaves your device",
    "Free to use, with no registration or installation required",
    "Option to copy or download the formatted JSON output",
  ]}
  inputTitle="Input XML:"
  inputExample={`<user>
  <name>John Doe</name>
  <age>30</age>
  <emails>
    <email>john@example.com</email>
  </emails>
  <isActive>true</isActive>
</user>`}
  outputTitle="Output JSON:"
  outputExample={`{
  "user": {
    "name": "John Doe",
    "age": 30,
    "emails": {
      "email": "john@example.com"
    },
    "isActive": true
  }
}`}
  callToAction="Start converting your XML to JSON instantly — no signup required."
/>

    </div>
  );
};

export default XmlToJson;
